import { Controller } from '@hotwired/stimulus'
import { patch } from '@rails/request.js'

const MENU_MIN_WIDTH = 192
const MENU_MAX_HEIGHT = 256
const VIEWPORT_PADDING = 8

// FileTag picker for FileGroup cards (task / BC attachments).
// Menu is portaled to document.body so it is not clipped by card/section overflow.
export default class extends Controller {
  static targets = ['chips', 'button']
  static values = {
    fileId: Number,
    updateUrl: String,
    tagsUrl: { type: String, default: '/tags.json?context=file' },
    selected: { type: Array, default: [] },
    manageUrl: { type: String, default: '/tags?context=file' },
    emptyMessage: { type: String, default: '' },
    manageMessage: { type: String, default: '' },
    loadingMessage: { type: String, default: '' }
  }

  connect() {
    this._selected = this._normalizeTags(this.selectedValue)
    this._allTags = null
    this._saving = false
    this._open = false
    this._menu = null
    this._list = null
    this._boundOutsideClick = this._onOutsideClick.bind(this)
    this._boundReposition = this._positionMenu.bind(this)
    this._boundMenuChange = this._onMenuChange.bind(this)
    this._renderChips()
  }

  disconnect() {
    this._teardownMenu()
  }

  async toggleMenu(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this._open) {
      this._closeMenu()
      return
    }

    await this._openMenu()
  }

  async _openMenu() {
    this._ensureMenuElement()
    this._open = true
    this.buttonTarget?.setAttribute('aria-expanded', 'true')
    this._menu.classList.add('show')
    this._menu.style.display = 'block'
    this._positionMenu()

    if (!this._allTags) {
      this._list.innerHTML = `<div class="small text-muted px-2 py-1">${this._escape(this.loadingMessageValue || '…')}</div>`
      try {
        const response = await fetch(this.tagsUrlValue, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        })
        const data = response.ok ? await response.json() : []
        this._allTags = Array.isArray(data) ? data : []
      } catch {
        this._allTags = []
      }
    }

    this._renderList()
    this._positionMenu()

    document.addEventListener('click', this._boundOutsideClick)
    window.addEventListener('resize', this._boundReposition)
    window.addEventListener('scroll', this._boundReposition, true)
  }

  _closeMenu() {
    this._open = false
    this.buttonTarget?.setAttribute('aria-expanded', 'false')
    if (this._menu) {
      this._menu.classList.remove('show')
      this._menu.style.display = 'none'
    }
    document.removeEventListener('click', this._boundOutsideClick)
    window.removeEventListener('resize', this._boundReposition)
    window.removeEventListener('scroll', this._boundReposition, true)
  }

  _teardownMenu() {
    this._closeMenu()
    if (this._menu) {
      this._menu.removeEventListener('change', this._boundMenuChange)
      this._menu.remove()
      this._menu = null
      this._list = null
    }
  }

  _ensureMenuElement() {
    if (this._menu) return

    this._menu = document.createElement('div')
    this._menu.className = 'dropdown-menu p-2 shadow file-attachment-tag-menu'
    this._menu.style.minWidth = `${MENU_MIN_WIDTH}px`
    this._menu.style.maxHeight = `${MENU_MAX_HEIGHT}px`
    this._menu.style.overflowY = 'auto'
    this._menu.style.zIndex = '1080'
    this._menu.style.display = 'none'
    this._menu.addEventListener('click', event => event.stopPropagation())
    this._menu.addEventListener('change', this._boundMenuChange)

    this._list = document.createElement('div')
    this._menu.appendChild(this._list)
    document.body.appendChild(this._menu)
  }

  _positionMenu() {
    if (!this._menu || !this.hasButtonTarget) return

    const rect = this.buttonTarget.getBoundingClientRect()
    const menuHeight = this._menu.offsetHeight || 96
    const menuWidth = this._menu.offsetWidth || MENU_MIN_WIDTH
    const gap = 4
    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap
    const openUp = spaceBelow < menuHeight && spaceAbove > spaceBelow

    let top = openUp ? rect.top - menuHeight - gap : rect.bottom + gap
    let left = rect.left

    left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - menuWidth - VIEWPORT_PADDING))
    top = Math.max(VIEWPORT_PADDING, Math.min(top, window.innerHeight - menuHeight - VIEWPORT_PADDING))

    this._menu.style.position = 'fixed'
    this._menu.style.top = `${top}px`
    this._menu.style.left = `${left}px`
  }

  _onOutsideClick(event) {
    if (this.element.contains(event.target)) return
    if (this._menu?.contains(event.target)) return
    this._closeMenu()
  }

  _onMenuChange(event) {
    const input = event.target
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return

    const tagId = Number(input.dataset.tagId)
    if (!tagId) return

    this._toggleTag(tagId)
  }

  async _toggleTag(tagId) {
    if (this._saving) return

    const previous = this._selected.map(tag => ({ ...tag }))
    const nextIds = new Set(this._selected.map(tag => tag.id))
    if (nextIds.has(tagId)) nextIds.delete(tagId)
    else nextIds.add(tagId)

    this._selected = (this._allTags || []).filter(tag => nextIds.has(Number(tag.id))).map(tag => ({
      id: Number(tag.id),
      tag: tag.tag,
      color: tag.color
    }))
    this._renderChips()
    this._renderList()

    this._saving = true
    try {
      const response = await patch(this.updateUrlValue, {
        body: JSON.stringify({ tag_ids: Array.from(nextIds) }),
        contentType: 'application/json',
        responseKind: 'json'
      })

      if (!response.ok) {
        this._selected = previous
        this._renderChips()
        this._renderList()
        return
      }

      const payload = await response.json
      this._selected = this._normalizeTags(payload.tags || [])
      this.selectedValue = this._selected
      this._renderChips()
      this._renderList()
    } catch {
      this._selected = previous
      this._renderChips()
      this._renderList()
    } finally {
      this._saving = false
    }
  }

  _normalizeTags(tags) {
    return Array.isArray(tags)
      ? tags.map(tag => ({
          id: Number(tag.id),
          tag: tag.tag,
          color: tag.color
        }))
      : []
  }

  _selectedIds() {
    return new Set(this._selected.map(tag => tag.id))
  }

  _renderChips() {
    if (!this.hasChipsTarget) return

    this.chipsTarget.innerHTML = this._selected.map(tag => {
      const color = tag.color || '#3498db'
      return `<span class="badge rounded-pill file-tag-badge"
                    style="background-color: ${this._escape(color)}; color: ${this._contrastColor(color)};">
                ${this._escape(tag.tag)}
              </span>`
    }).join('')
  }

  _renderList() {
    if (!this._list) return

    if (!this._allTags?.length) {
      this._list.innerHTML = `
        <div class="small text-muted px-2 py-2 text-wrap" style="max-width: 16rem; white-space: normal;">
          <div>${this._escape(this.emptyMessageValue)}</div>
          <a href="${this._escape(this.manageUrlValue)}" class="btn btn-sm btn-link p-0 mt-1 text-start">
            ${this._escape(this.manageMessageValue)}
          </a>
        </div>`
      return
    }

    const selected = this._selectedIds()
    this._list.innerHTML = this._allTags.map(tag => {
      const id = Number(tag.id)
      const color = tag.color || '#3498db'
      const checked = selected.has(id) ? 'checked' : ''
      return `
        <label class="dropdown-item d-flex align-items-center gap-2 small rounded py-1 px-2">
          <input type="checkbox"
                 class="form-check-input mt-0"
                 ${checked}
                 data-tag-id="${id}">
          <span class="badge rounded-pill"
                style="background-color: ${this._escape(color)}; color: ${this._contrastColor(color)};">
            ${this._escape(tag.tag)}
          </span>
        </label>`
    }).join('')
  }

  _escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  _contrastColor(hex) {
    const h = String(hex || '').replace('#', '')
    if (h.length < 6) return '#333'
    const r = parseInt(h.substring(0, 2), 16)
    const g = parseInt(h.substring(2, 4), 16)
    const b = parseInt(h.substring(4, 6), 16)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255.0
    return luminance > 0.55 ? '#333' : '#fff'
  }
}
